/**
 * Full-screen recording overlay (workstream V, walkie-talkie UX).
 *
 * One overlay, two entry modes:
 *   walkie — entered by press-and-hold (mic button or chat surface).
 *            Release sends, slide up locks hands-free, slide sideways
 *            cancels. While the finger is down the overlay is pointer-
 *            transparent: the gesture is tracked at document level by
 *            the input component that owns the recorder.
 *   locked — entered by a plain tap on the mic button (or by sliding up
 *            from walkie). Giant Send / Cancel targets — the whole point
 *            is that stopping a recording should not require aiming.
 *
 * The waveform is a rolling RMS history off an AnalyserNode — not
 * decoration: on a loud street it's the trust signal that the mic
 * actually hears you.
 */

import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronUp, Lock, Mic, Send, X } from 'lucide-react';

const LOCK_DRAG_PX = 90; // keep in sync with the gesture tracker in ChatSection

function formatElapsed(ms) {
  const total = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/** Rolling-history waveform, WhatsApp/voice-memo style. */
const Waveform = ({ stream }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx2d = canvas.getContext('2d');
    let raf = null;
    let audioCtx = null;
    let analyser = null;
    let data = null;
    const history = [];
    const BARS = 48;

    if (stream) {
      try {
        const Ctor = window.AudioContext || window.webkitAudioContext;
        audioCtx = new Ctor();
        const source = audioCtx.createMediaStreamSource(stream);
        analyser = audioCtx.createAnalyser();
        analyser.fftSize = 512;
        source.connect(analyser);
        data = new Uint8Array(analyser.fftSize);
      } catch (_) {
        analyser = null;
      }
    }

    let frame = 0;
    const draw = () => {
      raf = requestAnimationFrame(draw);
      frame += 1;

      // Sample RMS every other frame (~30Hz) so the strip scrolls calmly.
      if (frame % 2 === 0) {
        let rms = 0;
        if (analyser && data) {
          analyser.getByteTimeDomainData(data);
          let sum = 0;
          for (let i = 0; i < data.length; i += 1) {
            const v = (data[i] - 128) / 128;
            sum += v * v;
          }
          rms = Math.sqrt(sum / data.length);
        }
        history.push(rms);
        if (history.length > BARS) history.shift();
      }

      const { width, height } = canvas;
      ctx2d.clearRect(0, 0, width, height);
      const slot = width / BARS;
      const barW = Math.max(2, slot * 0.55);
      const mid = height / 2;
      const hasRoundRect = typeof ctx2d.roundRect === 'function';
      for (let i = 0; i < history.length; i += 1) {
        // Perceptual boost: speech RMS lives around 0.05-0.3.
        const level = Math.min(1, history[i] * 3.2);
        const h = Math.max(3, level * (height * 0.9));
        const x = width - (history.length - i) * slot + (slot - barW) / 2;
        const alpha = 0.35 + 0.65 * (i / BARS);
        ctx2d.fillStyle = `rgba(52, 211, 153, ${alpha.toFixed(2)})`; // emerald-400
        if (hasRoundRect) {
          ctx2d.beginPath();
          ctx2d.roundRect(x, mid - h / 2, barW, h, barW / 2);
          ctx2d.fill();
        } else {
          ctx2d.fillRect(x, mid - h / 2, barW, h);
        }
      }
    };
    draw();

    return () => {
      if (raf) cancelAnimationFrame(raf);
      try {
        if (audioCtx && audioCtx.state !== 'closed') audioCtx.close();
      } catch (_) {}
    };
  }, [stream]);

  return (
    <canvas
      ref={canvasRef}
      width={640}
      height={160}
      className="w-full max-w-sm h-24"
      aria-hidden="true"
    />
  );
};

const VoiceRecordingOverlay = ({
  mode, // 'walkie' | 'locked'
  stream,
  startedAt, // ms epoch, null while the mic is still opening
  drag = { dy: 0, dx: 0 }, // live gesture offsets (walkie only)
  onSend,
  onCancel,
}) => {
  const { i18n } = useTranslation();
  const de = (i18n?.language || '').toLowerCase().startsWith('de');
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(t);
  }, []);

  const walkie = mode === 'walkie';
  const lockProgress = walkie ? Math.min(1, drag.dy / LOCK_DRAG_PX) : 0;
  const cancelHinting = walkie && drag.dx > 40 && drag.dx > drag.dy;

  return (
    <div
      // z must clear Leaflet's internal panes/controls (~1000); the camera
      // modal, the closest sibling takeover, sits at 9999 — match it.
      className={`fixed inset-0 z-[9999] flex flex-col items-center justify-between bg-gray-950/90 backdrop-blur-md select-none ${
        walkie ? 'pointer-events-none' : 'pointer-events-auto'
      }`}
      role="dialog"
      aria-label={de ? 'Sprachaufnahme' : 'Voice recording'}
    >
      {/* Lock affordance (walkie): grows as the finger slides up. */}
      <div className="pt-12 flex flex-col items-center gap-1 h-28">
        {walkie && (
          <>
            <ChevronUp
              size={20}
              className="text-white/50 animate-bounce"
              style={{ opacity: 1 - lockProgress }}
            />
            <div
              className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center transition-transform"
              style={{ transform: `scale(${1 + lockProgress * 0.5})` }}
            >
              <Lock
                size={20}
                className={lockProgress >= 1 ? 'text-emerald-400' : 'text-white/70'}
              />
            </div>
            <span className="text-white/50 text-xs mt-1">
              {de ? 'Hochwischen zum Sperren' : 'Slide up to lock'}
            </span>
          </>
        )}
      </div>

      {/* Center: waveform + timer */}
      <div className="flex flex-col items-center gap-4 px-8 w-full">
        <Waveform stream={stream} />
        <div className="flex items-center gap-2.5">
          <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse" />
          <span className="text-white font-mono text-2xl tabular-nums">
            {startedAt ? formatElapsed(now - startedAt) : '0:00'}
          </span>
        </div>
        {!startedAt && (
          <span className="text-white/40 text-sm">
            {de ? 'Mikrofon wird geöffnet …' : 'Opening microphone …'}
          </span>
        )}
      </div>

      {/* Bottom: mode-specific controls / hints */}
      <div className="pb-14 flex flex-col items-center gap-5 px-8 w-full">
        {walkie ? (
          <>
            <div
              className={`w-24 h-24 rounded-full flex items-center justify-center transition-colors ${
                cancelHinting ? 'bg-red-500/80' : 'bg-emerald-500'
              }`}
            >
              {cancelHinting ? (
                <X size={40} className="text-white" />
              ) : (
                <Mic size={40} className="text-white" />
              )}
            </div>
            <div className="text-center">
              <p className="text-white text-lg font-semibold">
                {cancelHinting
                  ? de
                    ? 'Loslassen zum Abbrechen'
                    : 'Release to cancel'
                  : de
                    ? 'Loslassen zum Senden'
                    : 'Release to send'}
              </p>
              <p className="text-white/40 text-xs mt-1.5">
                {de
                  ? 'Zur Seite wischen zum Abbrechen'
                  : 'Slide sideways to cancel'}
              </p>
            </div>
          </>
        ) : (
          <>
            <p className="text-white/70 text-sm">
              {de ? 'Aufnahme läuft …' : 'Recording …'}
            </p>
            <div className="flex items-center gap-8">
              <button
                type="button"
                onClick={onCancel}
                className="w-16 h-16 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white/80"
                aria-label={de ? 'Aufnahme verwerfen' : 'Discard recording'}
              >
                <X size={26} />
              </button>
              <button
                type="button"
                onClick={onSend}
                className="w-24 h-24 rounded-full bg-emerald-500 hover:bg-emerald-400 flex items-center justify-center text-white shadow-xl shadow-emerald-500/30"
                aria-label={de ? 'Aufnahme senden' : 'Send recording'}
              >
                <Send size={36} />
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default VoiceRecordingOverlay;
